import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {isEmpty, isNil} from 'ramda';

import {useAttachTelephonyLineNumberMutation, useGetTwilioNumbersQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface AttachTelephonyNumberFormProps extends RequiredTestIdProps {
  lineId: string;
}

const attachTelephonyNumberSchema = z.object({
  numberSid: z.string().min(1),
});

type AttachTelephonyNumberFormValues = z.infer<typeof attachTelephonyNumberSchema>;

export function AttachTelephonyNumberForm(props: AttachTelephonyNumberFormProps) {
  const {data: numbers, isLoading, isError} = useGetTwilioNumbersQuery();

  const [attachNumber] = useAttachTelephonyLineNumberMutation();

  // A number routes to at most one line: attaching one already taken (by this or another line) is
  // rejected by the API, so only unattached numbers are offered.
  const options = (numbers?.numbers ?? [])
    .filter((number) => isNil(number.lineId))
    .map((number) => ({label: number.phoneNumber, value: number.sid}));

  const handleSubmit: FormSubmitHandler<AttachTelephonyNumberFormValues> = async (values) => {
    await attachNumber({lineId: props.lineId, numberSid: values.numberSid})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const hasNoAvailableNumbers = !isLoading && !isError && isEmpty(options);

  return hasNoAvailableNumbers ? (
    <VStack spacing={4}>
      <Alert
        variant="info"
        message={i18n.t('page.settings.telephony.noNumbersAvailable')}
        data-testid={suffixTestId('noNumbers', props)}
      />
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.close')}
          onClick={closeCurrentDialog}
          data-testid={suffixTestId('close', props)}
        />
      </ButtonGroup>
    </VStack>
  ) : (
    <Form<AttachTelephonyNumberFormValues>
      experimentalZodSchema={attachTelephonyNumberSchema}
      onSubmit={handleSubmit}
      defaultValues={{numberSid: ''}}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="numberSid"
            type="choice"
            label={i18n.t('entity.person.labels.phoneNumber')}
            options={options}
            isLoading={isLoading}
            isRequired
            menuInPortal
            data-testid={suffixTestId('numberSid', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={i18n.t('page.settings.telephony.connectNumber')}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
